import { useState } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import SideSection from '../constant/SideSection';
import { useNavigate } from 'react-router-dom';
import { useSignin } from '../hooks/useSignin';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  
  const { signin, googleSignin, appleSignin, loading, error, clearError } = useSignin();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    
    if (!email || !password) {
      return;
    }

    clearError();

    const result = await signin(email, password, rememberMe);
    
    if (result.success) {
      console.log('Login successful');
    }
  };

  const handleGoogleLogin = () => {
    clearError();
    googleSignin();
  };

  const handleAppleLogin = () => {
    clearError();
    appleSignin();
  };

  return (
    <div className='flex flex-col lg:flex-row min-h-screen'>
      {/* Side Section - only visible on large screens */}
      <div className="hidden w-1/2 lg:block">
        <SideSection imageUrl="/images/auth.png" />
      </div>
      
      {/* Main Content */}
      <div className="flex-1 lg:w-1/2 flex flex-col min-h-screen">
        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-[30rem] p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-semibold text-gray-900">Login to Your Account</h1>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  disabled={loading}
                  required
                />
              </div>

              {/* Password Field */}
              <div>
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <div className="ml-2 text-sm text-gray-600">
                    Remember Me
                  </div>
                </div>
                <button 
                  type="button"
                  onClick={() => navigate('/auth/update-password')} 
                  className="text-sm text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  Forgot Password
                </button>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading || !email || !password}
                className={`w-full py-3 px-4 rounded-md font-medium transition duration-200 ${
                  loading || !email || !password
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? 'Signing in...' : 'Login'}
              </button>

              {/* Or login with */}
              <div className="text-center">
                <span className="text-gray-500 text-sm">Or login with</span>
              </div>

              {/* Social Login Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>
                <button
                  type="button"
                  onClick={handleAppleLogin}
                  disabled={loading}
                  className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Apple
                </button>
              </div>

              {/* Create Account Link */}
              <div className="text-center">
                <span className="text-gray-600 text-sm">New Here? </span>
                <button 
                  type="button"
                  onClick={() => navigate('/auth/register')} 
                  className="text-gray-900 text-sm font-medium hover:underline"
                  disabled={loading}
                >
                  Create an Account
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer - Stays at bottom on large screens, resets on smaller screens */}
        <div className="lg:mt-0 mt-8">
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="text-center text-xs text-gray-500 space-x-4">
              <span>© 2024 GPay. All rights reserved.</span>
              <a href="#" className="hover:text-gray-700">Terms & Conditions</a>
              <a href="#" className="hover:text-gray-700">Privacy Policy</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}